import { useState, useRef, useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import { ListChecks, Loader2 } from "lucide-react";

interface StatusOption {
  value: string;
  label: string;
}

interface StatusChipStyle {
  label: string;
  className: string;
}

interface Props {
  options: StatusOption[];
  statusChipMap: Record<string, StatusChipStyle>;
  currentStatus: string;
  note?: string | null;
  fromLabel?: string | null;
  disabled?: boolean;
  disabledMessage?: string;
  onChange: (status: string, notes: string | null) => Promise<void>;
  children: ReactNode;
}

/** Click-to-toggle popover for changing a job/assignment's status directly from its
 * card, without opening the details modal. Mirrors the status-pill-then-note flow
 * used on the details page: pick a different status, optionally add a note, save. */
export default function StatusChangePopover({
  options, statusChipMap, currentStatus, note, fromLabel, disabled, disabledMessage, onChange, children,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [pendingNotes, setPendingNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEntered(false);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  function openPopover(e: React.MouseEvent) {
    e.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
    }
    setPendingStatus(null);
    setPendingNotes("");
    setError(null);
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setPendingStatus(null);
    setPendingNotes("");
    setError(null);
  }

  function selectStatus(status: string) {
    if (disabled || status === currentStatus) return;
    setPendingStatus(status);
    setPendingNotes("");
    setError(null);
  }

  function cancelPending() {
    setPendingStatus(null);
    setPendingNotes("");
    setError(null);
  }

  async function confirmChange() {
    if (!pendingStatus) return;
    setSaving(true);
    setError(null);
    try {
      await onChange(pendingStatus, pendingNotes.trim() || null);
      close();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to update status. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") close();
  }

  const currentLabel = statusChipMap[currentStatus]?.label ?? currentStatus;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPopover}
        className={`inline-flex shrink-0 rounded bg-transparent border-0 p-0 m-0 leading-none transition-all ${
          open ? "ring-2 ring-primary/50" : "hover:ring-1 hover:ring-primary/40"
        }`}
      >
        {children}
      </button>

      {open && pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={(e) => { e.stopPropagation(); close(); }} />
            <div
              ref={containerRef}
              tabIndex={-1}
              className={`fixed z-[81] w-80 origin-top-right transition-all duration-150 ease-out outline-none ${
                entered ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-1"
              }`}
              style={{ top: pos.top, right: pos.right }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
            >
              {/* Caret pointing back up to the trigger */}
              <div className="absolute -top-1.5 right-5 w-3 h-3 bg-surface-muted border-t border-l border-border/80 rotate-45" />

              <div className="relative bg-surface-muted border border-border/80 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
                <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-b from-surface/40 to-surface-muted border-b border-border/80">
                  <div className="w-7 h-7 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center shrink-0">
                    <ListChecks className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold text-foreground font-inter leading-tight">Change status</p>
                    <p className="text-[10.5px] text-muted-foreground font-inter leading-tight mt-0.5">
                      Currently {fromLabel ? `${fromLabel} → ${currentLabel}` : currentLabel}
                    </p>
                  </div>
                </div>

                <div className="p-3.5">
                  {disabled ? (
                    <p className="text-[11px] text-muted-foreground font-inter leading-snug py-1">
                      {disabledMessage ?? "This status is terminal and cannot be changed."}
                    </p>
                  ) : (
                    <>
                      {note && !pendingStatus && (
                        <p className="text-[11px] text-muted-foreground font-inter leading-snug italic mb-3">"{note}"</p>
                      )}

                      <div className="flex flex-wrap gap-1.5">
                        {options.map((opt) => {
                          const isCurrent = opt.value === currentStatus;
                          const isPending = pendingStatus === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => selectStatus(opt.value)}
                              disabled={isCurrent}
                              className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-all disabled:cursor-default ${
                                isPending
                                  ? `${statusChipMap[opt.value]?.className ?? "bg-muted text-foreground border-border"} ring-2 ring-white/20 ring-offset-1 ring-offset-surface-muted`
                                  : isCurrent
                                  ? statusChipMap[opt.value]?.className ?? "bg-muted text-foreground border-border"
                                  : "bg-transparent text-muted-foreground border-border hover:border-muted-foreground hover:text-foreground"
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>

                      {pendingStatus && (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={pendingNotes}
                            onChange={(e) => setPendingNotes(e.target.value)}
                            placeholder="Add a note for this status change… (optional)"
                            rows={2}
                            autoFocus
                            className="w-full bg-surface/60 border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder-muted-foreground font-inter resize-none focus:outline-none focus:border-primary/60 transition-colors"
                          />
                          {error && <p className="text-[11px] text-destructive font-inter">{error}</p>}
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={confirmChange}
                              disabled={saving}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
                            >
                              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                              {saving ? "Saving…" : `Save as "${statusChipMap[pendingStatus]?.label ?? pendingStatus}"`}
                            </button>
                            <button
                              type="button"
                              onClick={cancelPending}
                              disabled={saving}
                              className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground text-xs font-inter rounded-lg transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
